import React from 'react';
import { Link } from 'react-router-dom';
import { useDocumentMeta } from '../utils/useDocumentMeta';

/**
 * Terms of Service — required by Stripe (US merchant accounts), LinkedIn /
 * Meta / Google Ads, and as a baseline for limiting liability before paid
 * marketing scales.
 *
 * Plain-language; NOT legal advice. Recommend final review by counsel.
 */
const TermsOfService = () => {
  const lastUpdated = 'April 24, 2026';
  useDocumentMeta({
    title: 'Terms of Service — Ascend',
    description: "The terms governing your use of Ascend's AI resume optimization platform.",
    path: '/terms',
  });
  return (
    <>
      <div className="min-h-screen bg-white" data-testid="terms-page">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
          <Link to="/" className="text-sm text-[#002fa7] hover:underline mb-8 inline-block">← Back to Ascend</Link>

          <h1 className="font-satoshi text-4xl lg:text-5xl font-bold tracking-tight text-[#0f1115] mb-3">
            Terms of Service
          </h1>
          <p className="text-sm text-[#6b7280] mb-10">Last updated: {lastUpdated}</p>

          <div className="prose prose-lg max-w-none text-[#374151] leading-relaxed">

            <p className="text-lg">
              These Terms govern your use of Ascend (ascendforyou.net). By creating an account or using the Service you agree to them.
              If you do not agree, do not use the Service.
            </p>

            <h2 className="font-satoshi font-bold text-2xl text-[#0f1115] mt-12 mb-4">1. Eligibility</h2>
            <p>You must be at least 16 years old. By registering you confirm you are.</p>

            <h2 className="font-satoshi font-bold text-2xl text-[#0f1115] mt-12 mb-4">2. Your account</h2>
            <p>You are responsible for keeping your password secret and for all activity on your account. Notify us at hello@ascendforyou.net if you suspect unauthorized access.</p>

            <h2 className="font-satoshi font-bold text-2xl text-[#0f1115] mt-12 mb-4">3. Subscriptions and payments</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>Payments are processed by Stripe. By subscribing you agree to Stripe&rsquo;s terms in addition to these.</li>
              <li>Yearly Pro plans include a 14-day free trial. You may cancel any time before the trial ends to avoid charges.</li>
              <li>Subscriptions auto-renew at the end of each billing period. You may cancel from your dashboard or by emailing us.</li>
              <li>Promotional discounts (e.g., the Founding Member 40% off) apply only to the first billing year unless stated otherwise. Renewal reverts to standard pricing.</li>
              <li>All prices are in USD unless stated. Applicable taxes may be added at checkout.</li>
            </ul>

            <h2 className="font-satoshi font-bold text-2xl text-[#0f1115] mt-12 mb-4">4. Refunds</h2>
            <p>
              We offer a 7-day, no-questions refund window for first-time monthly or yearly subscriptions if you have not generated more than 5 AI outputs.
              Refund requests beyond that window are reviewed case-by-case. To request a refund, email hello@ascendforyou.net within the window.
            </p>

            <h2 className="font-satoshi font-bold text-2xl text-[#0f1115] mt-12 mb-4">5. Acceptable use</h2>
            <p>You agree NOT to:</p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>Submit content you do not own or have permission to use.</li>
              <li>Use the Service for any unlawful purpose, including misrepresenting credentials in a job application.</li>
              <li>Share your account, scrape the Service, or attempt to reverse-engineer it.</li>
              <li>Upload malicious code or attempt to compromise the Service&rsquo;s security.</li>
              <li>Use the Service to generate content that defames, harasses, or violates the rights of others.</li>
            </ul>

            <h2 className="font-satoshi font-bold text-2xl text-[#0f1115] mt-12 mb-4">6. AI-generated content</h2>
            <p>
              Ascend uses AI to score, rewrite, and generate resume content based on your input. AI output may be inaccurate, may include hallucinated facts,
              and is provided for your review and editing. You are solely responsible for verifying the accuracy of any content you use in real applications.
              Ascend is not responsible for hiring outcomes, employer decisions, or claims arising from how you use AI-generated text.
            </p>

            <h2 className="font-satoshi font-bold text-2xl text-[#0f1115] mt-12 mb-4">7. Your content and license</h2>
            <p>
              You retain all rights to the resumes and content you create on Ascend. By using the Service you grant us a limited, non-exclusive, worldwide license to host,
              process, and display that content solely for the purpose of operating the Service for you. We will not sell or share it for advertising. We do not use your
              resume content to train external AI models.
            </p>

            <h2 className="font-satoshi font-bold text-2xl text-[#0f1115] mt-12 mb-4">8. Termination</h2>
            <p>
              You may close your account at any time from the dashboard or by emailing us. We may suspend or terminate accounts that violate these Terms, with reasonable notice
              except in cases of fraud, security incidents, or legal demand. Upon termination your data is deleted within 30 days, except records we are required by law to retain.
            </p>

            <h2 className="font-satoshi font-bold text-2xl text-[#0f1115] mt-12 mb-4">9. Disclaimer of warranties</h2>
            <p className="uppercase text-sm tracking-wide">
              The Service is provided &ldquo;AS IS&rdquo; and &ldquo;AS AVAILABLE.&rdquo; We make no warranties, express or implied, including merchantability, fitness for a particular purpose,
              non-infringement, or that the Service will be uninterrupted or error-free.
            </p>

            <h2 className="font-satoshi font-bold text-2xl text-[#0f1115] mt-12 mb-4">10. Limitation of liability</h2>
            <p className="uppercase text-sm tracking-wide">
              To the maximum extent permitted by law, Ascend&rsquo;s total liability for any claim arising from or related to the Service will not exceed the greater of (a) the amount you
              paid Ascend in the 12 months preceding the claim, or (b) US$100. We are not liable for indirect, consequential, incidental, or special damages, including lost profits,
              lost data, or lost employment opportunities.
            </p>

            <h2 className="font-satoshi font-bold text-2xl text-[#0f1115] mt-12 mb-4">11. Indemnification</h2>
            <p>
              You agree to indemnify and hold Ascend harmless from any claims arising from (a) your violation of these Terms, (b) your content,
              or (c) your use of the Service in violation of any law.
            </p>

            <h2 className="font-satoshi font-bold text-2xl text-[#0f1115] mt-12 mb-4">12. Governing law and disputes</h2>
            <p>
              These Terms are governed by the laws of the State of Texas, USA, excluding conflict-of-law rules. Any dispute will be resolved in the state or federal courts located in
              Texas, and you consent to that jurisdiction. Where required by your local consumer law, you retain mandatory rights in your country of residence.
            </p>

            <h2 className="font-satoshi font-bold text-2xl text-[#0f1115] mt-12 mb-4">13. Changes to these Terms</h2>
            <p>
              We may update these Terms occasionally. Material changes are announced by email or in-app banner at least 14 days before taking effect.
              Continued use of the Service after a change constitutes acceptance of the revised Terms.
            </p>

            <h2 className="font-satoshi font-bold text-2xl text-[#0f1115] mt-12 mb-4">14. Contact</h2>
            <p>
              📧 <a href="mailto:hello@ascendforyou.net" className="text-[#002fa7] underline">hello@ascendforyou.net</a>
              <br />
              🌐 <a href="https://ascendforyou.net" className="text-[#002fa7] underline">ascendforyou.net</a>
            </p>

          </div>

          <div className="mt-16 pt-8 border-t border-[#e5e7eb] flex flex-wrap gap-4 text-sm">
            <Link to="/" className="text-[#4b5563] hover:text-[#002fa7]">Home</Link>
            <Link to="/privacy" className="text-[#4b5563] hover:text-[#002fa7]">Privacy Policy</Link>
            <Link to="/pricing" className="text-[#4b5563] hover:text-[#002fa7]">Pricing</Link>
            <a href="mailto:hello@ascendforyou.net" className="text-[#4b5563] hover:text-[#002fa7]">Contact</a>
          </div>
        </div>
      </div>
    </>
  );
};

export default TermsOfService;
